'use client';

import styled from 'styled-components';
import { Text } from '@lmn/design-system';

export const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.kallisto.space.space150};
  width: 100%;
`;

export const CalendarDaysWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.kallisto.space.space100};
  width: 100%;
`;

export const MonthNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  background: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
  border-radius: 8px;
  cursor: pointer;
  padding: 12px;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const MonthNavRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const DayHeaderRow = styled.div`
  display: flex;
  width: 100%;
`;

export const DayHeaderCell = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
`;

export const WeeksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

export const WeekRow = styled.div`
  display: flex;
  width: 100%;
`;

export const DayCell = styled.div<{
  $isSelected: boolean;
  $isCheapest: boolean;
  $isPast: boolean;
  $isEmpty: boolean;
}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 8px;
  min-width: 0;
  min-height: 48px;
  visibility: ${(p) => (p.$isEmpty ? 'hidden' : 'visible')};
  cursor: ${(p) => (p.$isPast || p.$isEmpty ? 'default' : 'pointer')};

  ${(p) =>
    p.$isSelected && `box-shadow: inset 0 0 0 2px ${p.theme.kallisto.color.border.interactive.borderPrimaryDefault};`}

  ${(p) => p.$isCheapest && `background: ${p.theme.kallisto.color.background.static.backgroundSuccessSubtle};`}
`;

export const DayHeaderText = styled(Text).attrs({ variant: 'Caption' })`
  color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
  text-align: center;
`;

export const DayNumber = styled(Text).attrs({ variant: 'Body', weight: 'bold' })<{
  $isPast: boolean;
  $isCheapest: boolean;
  $isUnavailable?: boolean;
}>`
  margin-bottom: -2px;
  text-align: center;
  color: ${(p) =>
    p.$isPast
      ? p.theme.kallisto.color.text.static.textDisabled
      : p.$isUnavailable
        ? p.theme.kallisto.color.text.static.textSecondary
        : p.$isCheapest
          ? p.theme.kallisto.color.text.static.textSuccess
          : p.theme.kallisto.color.text.static.textPrimary};
`;

export const PriceText = styled(Text).attrs({ variant: 'Caption' })<{ $isCheapest: boolean; $isPast: boolean }>`
  text-align: center;
  white-space: nowrap;
  color: ${(p) =>
    p.$isCheapest
      ? p.theme.kallisto.color.text.static.textSuccess
      : p.$isPast
        ? p.theme.kallisto.color.text.static.textDisabled
        : p.theme.kallisto.color.text.static.textPrimary};
`;

// The price area is a fixed height (the Caption line-height) so cells never
// shift as prices load. The loading skeleton, the past-date spacer and the
// sold-out label all reserve this exact height.
const PRICE_AREA_HEIGHT = '18px';

// Shown instead of the price once the rooms endpoint reports a day sold out: the apex
// price for that day is not bookable, so advertising it would be a lie. (MPH-887)
// The day stays clickable, so its text needs 4.5:1 and can't use the disabled grey. (MPH-1512)
export const UnavailableText = styled(Text).attrs({ variant: 'Caption' })`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${PRICE_AREA_HEIGHT};
  text-align: center;
  white-space: nowrap;
  color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
`;

export const PriceSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${PRICE_AREA_HEIGHT};
`;

// Past dates show no price; the spacer keeps their day number vertically
// aligned with the other cells.
export const PriceSpacer = styled.div`
  height: ${PRICE_AREA_HEIGHT};
`;

export const CalendarFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// The toggle is optional (it only shows where the price-toggler test can drive the funnel), so the
// row spaces its children apart instead of anchoring the legend right: with the toggle the legend
// sits at the far edge, without it the legend takes the row's start, as the design has it. (MPH-1003)
export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const PriceLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CheapestBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${(p) => p.theme.kallisto.color.background.static.backgroundSuccessSubtle};
`;

export const CheapestBadgeText = styled(Text).attrs({ variant: 'BodySmall', weight: 'bold' })`
  color: ${(p) => p.theme.kallisto.color.text.static.textSuccess};
`;

export const Divider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.commons.colors.gray100};
`;
